/**
 * Pet Page - Visual Page Builder
 *
 * Allows managing all content blocks of each organization's vida_pet website.
 * Panels: section tree (left) → ordered block list (center) → structured editor (right).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  DocumentTextIcon,
  FolderIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  Squares2X2Icon,
  ChevronRightIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import { contentApi } from '@/services/api';
import { PageLoader, Modal, ConfirmDialog, EmptyState } from '@/components/common';
import {
  COMPONENT_TYPES,
  COMPONENT_CATEGORIES,
  getComponentType,
  type ComponentCategory,
  type ComponentType,
} from '@/data/componentTypes';
import RichTextEditor from '@/components/RichTextEditor';
import NavConfigEditor from '@/components/cms/NavConfigEditor';
import FooterConfigEditor from '@/components/cms/FooterConfigEditor';
import PetSiteEditor from '@/components/cms/PetSiteEditor';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CMSSection {
  id: string;
  label: string;
  subsections?: { id: string; label: string }[];
}

interface ContentDocument {
  _id: string;
  section: string;
  component: string;
  content: Record<string, unknown>;
  locale: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Helper: detect image field key ──────────────────────────────────────────

function isImageKey(key: string): boolean {
  const k = key.toLowerCase();
  return k.includes('image') || k.includes('img') || k.includes('src') || k.includes('logo') || k.includes('cover') || k.includes('photo') || k.includes('banner') || k.includes('avatar');
}

// ─── Helper: friendly Spanish labels for common field keys ──────────────────

const FIELD_LABELS: Record<string, string> = {
  title: 'Título',
  subtitle: 'Subtítulo',
  description: 'Descripción',
  text: 'Texto',
  content: 'Contenido',
  name: 'Nombre',
  label: 'Etiqueta',
  paragraphs: 'Párrafos',
  buttonText: 'Texto del botón',
  buttonLink: 'Enlace del botón',
  linkUrl: 'URL del enlace',
  link: 'Enlace',
  url: 'URL',
  backgroundImage: 'Imagen de fondo',
  backgroundSrc: 'Fondo',
  imageSrc: 'Imagen',
  imageAlt: 'Texto alternativo',
  image: 'Imagen',
  logo: 'Logo',
  icon: 'Icono',
  backgroundColor: 'Color de fondo',
  textColor: 'Color del texto',
  role: 'Rol',
  author: 'Autor',
  date: 'Fecha',
  price: 'Precio',
  period: 'Periodo',
  features: 'Características',
  ctaText: 'Texto del botón',
  ctaLink: 'Enlace del botón',
  cards: 'Tarjetas',
  items: 'Elementos',
  slides: 'Diapositivas',
  regions: 'Regiones',
  testimonials: 'Testimonios',
  plans: 'Planes',
  brands: 'Marcas',
  sponsors: 'Patrocinadores',
  links: 'Enlaces',
  images: 'Imágenes',
  options: 'Opciones',
  caption: 'Leyenda',
  alt: 'Texto alternativo',
  src: 'Imagen',
  location: 'Ubicación',
  mapUrl: 'URL del mapa',
  videoUrl: 'URL del vídeo',
  locale: 'Idioma',
  order: 'Orden',
  mainTitle: 'Título principal',
  subtext: 'Texto secundario',
  gridTitle: 'Título de la sección',
  websiteUrl: 'Sitio web',
  previewImageUrl: 'Imagen de vista previa',
  readTime: 'Tiempo de lectura',
  breadcrumb: 'Miga de pan',
  sections: 'Secciones',
  details: 'Detalles',
  faqs: 'Preguntas frecuentes',
  question: 'Pregunta',
  answer: 'Respuesta',
  articles: 'Artículos',
  designation: 'Cargo',
  stars: 'Estrellas',
  height: 'Alto',
  width: 'Ancho',
  type: 'Tipo',
  heading: 'Encabezado',
  body: 'Cuerpo',
  intro: 'Introducción',
  linkText: 'Texto del enlace',
  imageUrl: 'Imagen',
  videoId: 'ID del vídeo',
  className: 'Clase CSS (avanzado)',
};

function getFieldLabel(key: string): string {
  return FIELD_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
}

// Ayudas cortas bajo los campos clave (guía para usuarios no técnicos).
const FIELD_HELP: Record<string, string> = {
  title: 'El título grande de esta sección.',
  subtitle: 'Un texto pequeño encima o debajo del título.',
  description: 'Texto descriptivo. Se muestra tal cual, sin formato.',
  buttonText: 'Lo que pone el botón (p. ej. «Saber más»).',
  buttonLink: 'A dónde lleva el botón: una página del sitio (/contacto) o un enlace completo.',
  ctaText: 'Lo que pone el botón.',
  ctaLink: 'A dónde lleva el botón.',
  linkText: 'El texto visible del enlace.',
  link: 'A dónde lleva: una página del sitio o un enlace completo.',
  url: 'Dirección completa (https://…).',
  websiteUrl: 'Dirección de la web (https://…).',
  imageSrc: 'Sube una imagen desde tu equipo o pega su enlace.',
  image: 'Sube una imagen desde tu equipo o pega su enlace.',
  backgroundImage: 'Imagen que se ve de fondo.',
  imageAlt: 'Describe la imagen (para accesibilidad y buscadores).',
  alt: 'Describe la imagen (para accesibilidad y buscadores).',
  slug: 'Parte de la dirección web. Sin espacios ni acentos (p. ej. «geoparque»).',
  tags: 'Palabras clave. Añade una por elemento.',
  videoUrl: 'Enlace del vídeo (Vimeo/YouTube).',
  className: 'Avanzado: clases CSS. Déjalo en blanco si no sabes qué es.',
};

function getFieldHelp(key: string): string | undefined {
  return FIELD_HELP[key];
}

// Etiqueta de campo + ayuda opcional debajo.
function FieldLabel({ fieldKey }: { fieldKey: string }) {
  const help = getFieldHelp(fieldKey);
  return (
    <>
      <label className="label">{getFieldLabel(fieldKey)}</label>
      {help && <p className="text-[11px] text-gray-400 -mt-1 mb-1.5 leading-snug">{help}</p>}
    </>
  );
}

// Quita etiquetas HTML de un texto (para las vistas previas del panel).
function plainText(s: string): string {
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Devuelve un fragmento del CONTENIDO real del bloque (su título/texto), para que
// en la lista el usuario reconozca el bloque por lo que dice, no por su tipo.
function getBlockPreview(content: unknown): string {
  if (!content || typeof content !== 'object') return '';
  const c = content as Record<string, unknown>;
  const preferred = ['title', 'heading', 'mainTitle', 'name', 'label', 'question', 'subtitle', 'text', 'description'];
  for (const k of preferred) {
    if (typeof c[k] === 'string' && (c[k] as string).trim()) return plainText(c[k] as string);
  }
  // Primer string corto que haya.
  for (const v of Object.values(c)) {
    if (typeof v === 'string' && v.trim() && v.length < 200) return plainText(v);
  }
  // Primer elemento de un array de objetos con título.
  for (const v of Object.values(c)) {
    if (Array.isArray(v) && v[0] && typeof v[0] === 'object') {
      const first = v[0] as Record<string, unknown>;
      for (const k of preferred) {
        if (typeof first[k] === 'string' && (first[k] as string).trim()) return plainText(first[k] as string);
      }
    }
  }
  return '';
}

// Devuelve la primera imagen del contenido del bloque (para la miniatura en la
// lista): así se reconoce cada bloque por su foto real, no solo por el icono.
function getBlockImage(content: unknown): string | null {
  const imgKeys = ['imageSrc', 'backgroundImage', 'image', 'featuredImage', 'backgroundSrc', 'previewImageUrl', 'logoSrc', 'imageUrl', 'src'];
  const looksLikeImg = (s: unknown): s is string =>
    typeof s === 'string' && /^(\/|https?:\/\/)/.test(s.trim());
  const walk = (v: unknown): string | null => {
    if (Array.isArray(v)) {
      for (const it of v) { const r = walk(it); if (r) return r; }
      return null;
    }
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      for (const k of imgKeys) if (looksLikeImg(o[k])) return o[k] as string;
      for (const val of Object.values(o)) { const r = walk(val); if (r) return r; }
    }
    return null;
  };
  return walk(content);
}

// Componentes cuya plantilla PET renderiza el texto como HTML de verdad
// (dangerouslySetInnerHTML). Solo estos deben ofrecer editor rich text; el resto
// se edita en texto plano para no colar etiquetas «<p>» que se verían literales.
const ALLOW_HTML_COMPONENTS = new Set<string>(['VimeoWithText']);

// Mapa sección del CMS → ruta pública del PET (espejo del PATH_TO_SECTION de la
// plantilla vida_pet). Se usa para que la vista previa muestre la MISMA página
// que se está editando, y así el click-to-edit case con los bloques visibles.
const SECTION_TO_PATH: Record<string, string> = {
  Home: '',
  Geoparque: 'que-ver/geoparque',
  Guadix: 'que-ver/guadix',
  Marquesado: 'que-ver/marquesado',
  ValleAlhama: 'que-ver/valledelalhama',
  ValleFardes: 'que-ver/valledelfardes',
  ValleGor: 'que-ver/valledelgor',
  Ecoturismo: 'que-hacer/ecoturismo',
  Galeria: 'que-hacer/galeria',
  Patrimonio: 'que-hacer/patrimonio',
  ProductosLocales: 'que-hacer/productoslocales',
  Termalismo: 'que-hacer/termalismo',
  Tradiciones: 'que-hacer/tradiciones',
  Trogloditismo: 'que-hacer/trogloditismo',
  TurismoActivo: 'que-hacer/turismoactivo',
  AsociacionCuevas: 'consejos/asociacioncuevas',
  CentrosInterpretacion: 'consejos/centrosdeinterpretacion',
  EstacionPaleontologica: 'consejos/estacionpaleontologica',
  OficinaTurismoGuadix: 'consejos/oficinadeturismo',
};

// ─── Recursive field renderer ─────────────────────────────────────────────────

interface FieldRendererProps {
  fieldKey: string;
  value: unknown;
  path: (string | number)[];
  editing: boolean;
  canUpdate: boolean;
  orgId: string;
  sectionId: string;
  // Solo los componentes que la plantilla PET pinta como HTML de verdad
  // (VimeoWithText) admiten un editor rich text. En el resto, el texto se pinta
  // en plano, así que el editor debe producir texto plano y NO HTML: si no, el
  // usuario vería «<p>...</p>» literal en la web.
  allowHtml: boolean;
  onChange: (path: (string | number)[], key: string, value: unknown) => void;
}

function FieldRenderer({ fieldKey, value, path, editing, canUpdate, orgId, sectionId, allowHtml, onChange }: FieldRendererProps) {
  // Qué elementos de una lista están desplegados (acordeón). Vacío = todos plegados.
  const [openItems, setOpenItems] = useState<Set<number>>(() => new Set());

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const ext = file.name.split('.').pop();
      const objectName = `images/${sectionId}/${Date.now()}.${ext}`;
      const { data } = await contentApi.getUploadUrl(orgId, objectName);
      await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      onChange(path, fieldKey, data.publicUrl);
      toast.success('Imagen subida');
    } catch {
      toast.error('Error al subir imagen');
    }
  };

  // paragraphs array → rich text editor
  if (fieldKey === 'paragraphs' && Array.isArray(value)) {
    const html = (value as string[]).map(p => `<p>${p}</p>`).join('');
    return (
      <div className="mb-4">
        <FieldLabel fieldKey={fieldKey} />
        {editing ? (
          <RichTextEditor
            value={html}
            onChange={(newHtml) => {
              // Convert HTML paragraphs back to string array
              const div = document.createElement('div');
              div.innerHTML = newHtml;
              const paragraphs = Array.from(div.querySelectorAll('p, h1, h2, h3, li'))
                .map(el => el.textContent ?? '')
                .filter(t => t.length > 0);
              onChange(path, fieldKey, paragraphs.length > 0 ? paragraphs : ['']);
            }}
            disabled={!editing}
          />
        ) : (
          <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
            {(value as string[]).join('\n') || <span className="text-gray-400 italic">Sin contenido</span>}
          </div>
        )}
      </div>
    );
  }

  // array of items — lista con elementos plegables (acordeón) + reordenar
  if (Array.isArray(value)) {
    const arr = value as unknown[];

    const move = (idx: number, dir: -1 | 1) => {
      const next = [...arr];
      const t = idx + dir;
      if (t < 0 || t >= next.length) return;
      [next[idx], next[t]] = [next[t], next[idx]];
      onChange(path, fieldKey, next);
    };
    const removeAt = (idx: number) => {
      const next = [...arr];
      next.splice(idx, 1);
      onChange(path, fieldKey, next);
    };
    const toggle = (idx: number) =>
      setOpenItems((prev) => {
        const n = new Set(prev);
        if (n.has(idx)) n.delete(idx); else n.add(idx);
        return n;
      });
    const addItem = () => {
      const template = arr[0] || {};
      const newItem =
        typeof template === 'object' && template !== null
          ? Object.fromEntries(Object.keys(template as Record<string, unknown>).map((k) => [k, '']))
          : '';
      onChange(path, fieldKey, [...arr, newItem]);
      // Abre el elemento recién añadido para editarlo directamente.
      setOpenItems((prev) => new Set(prev).add(arr.length));
    };

    // Botones de reordenar/eliminar de cada fila.
    const rowActions = (idx: number) =>
      editing && canUpdate ? (
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20" title="Subir">
            <ChevronUpIcon className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => move(idx, 1)} disabled={idx === arr.length - 1} className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20" title="Bajar">
            <ChevronDownIcon className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => removeAt(idx)} className="p-0.5 text-red-400 hover:text-red-600" title="Eliminar">
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null;

    return (
      <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
          {getFieldLabel(fieldKey)} <span className="text-gray-400 normal-case font-normal">({arr.length})</span>
        </h4>
        <div className="space-y-1.5">
          {arr.map((item, idx) => {
            const isObj = typeof item === 'object' && item !== null;
            if (!isObj) {
              // Elemento simple (texto): editable en línea, sin plegar.
              return (
                <div key={idx} className="flex items-center gap-1 bg-white rounded border border-gray-100 px-2 py-1.5">
                  <input
                    type="text"
                    value={String(item ?? '')}
                    onChange={(e) => {
                      const next = [...arr];
                      next[idx] = e.target.value;
                      onChange(path, fieldKey, next);
                    }}
                    className="input text-sm flex-1"
                    disabled={!editing}
                  />
                  {rowActions(idx)}
                </div>
              );
            }
            const preview = getBlockPreview(item) || `Elemento ${idx + 1}`;
            const open = openItems.has(idx);
            return (
              <div key={idx} className="bg-white rounded border border-gray-100">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button type="button" onClick={() => toggle(idx)} className="flex-1 flex items-center gap-1.5 min-w-0 text-left">
                    {open ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                    <span className="text-xs font-medium text-gray-700 truncate">{preview}</span>
                  </button>
                  {rowActions(idx)}
                </div>
                {open && (
                  <div className="px-2.5 pb-2.5 pt-1 border-t border-gray-100">
                    {Object.entries(item as Record<string, unknown>).map(([k, v]) => (
                      <FieldRenderer
                        key={k}
                        fieldKey={k}
                        value={v}
                        path={[...path, fieldKey, idx]}
                        editing={editing}
                        canUpdate={canUpdate}
                        orgId={orgId}
                        sectionId={sectionId}
                        allowHtml={allowHtml}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {editing && canUpdate && (
          <button onClick={addItem} className="btn-secondary text-xs mt-2 py-1">
            <PlusIcon className="w-3.5 h-3.5 mr-1" />
            Añadir elemento
          </button>
        )}
      </div>
    );
  }

  // nested object
  if (typeof value === 'object' && value !== null) {
    return (
      <div className="mb-4 p-3 border border-gray-200 rounded-lg">
        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center uppercase tracking-wide">
          <ChevronRightIcon className="w-3 h-3 mr-1" />{getFieldLabel(fieldKey)}
        </h4>
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <FieldRenderer
            key={k}
            fieldKey={k}
            value={v}
            path={[...path, fieldKey]}
            editing={editing}
            canUpdate={canUpdate}
            orgId={orgId}
            sectionId={sectionId}
            allowHtml={allowHtml}
            onChange={onChange}
          />
        ))}
      </div>
    );
  }

  // image field
  if (typeof value === 'string' && isImageKey(fieldKey)) {
    return (
      <div className="mb-4">
        <FieldLabel fieldKey={fieldKey} />
        <div className="flex items-start gap-3">
          {/* Vista previa grande (o marco vacío) */}
          <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
            {value ? (
              <img src={value} alt="" className="w-full h-full object-cover" />
            ) : (
              <PhotoIcon className="w-8 h-8 text-gray-300" />
            )}
          </div>
          {editing && canUpdate ? (
            <div className="flex-1 min-w-0">
              {/* Botón claro de subir desde el equipo */}
              <label className="btn-secondary text-sm cursor-pointer inline-flex items-center">
                <ArrowUpTrayIcon className="w-4 h-4 mr-1.5" />
                {value ? 'Cambiar imagen' : 'Subir imagen'}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
              {/* La URL queda como opción secundaria, plegada (menos técnico) */}
              <details className="mt-2">
                <summary className="text-xs text-gray-400 cursor-pointer select-none">o pega un enlace</summary>
                <input
                  type="text"
                  value={String(value ?? '')}
                  onChange={(e) => onChange(path, fieldKey, e.target.value)}
                  className="input text-xs mt-1"
                  placeholder="https://..."
                />
              </details>
            </div>
          ) : (
            <span className="text-sm text-gray-400 self-center">{value ? '' : 'Sin imagen'}</span>
          )}
        </div>
      </div>
    );
  }

  // long text / textarea hint
  const isLong =
    typeof value === 'string' &&
    (fieldKey.toLowerCase().includes('description') ||
      fieldKey.toLowerCase().includes('content') ||
      fieldKey.toLowerCase().includes('text') ||
      fieldKey.toLowerCase().includes('body') ||
      fieldKey.toLowerCase().includes('intro') ||
      (value as string).length > 80);

  if (isLong) {
    return (
      <div className="mb-4">
        <FieldLabel fieldKey={fieldKey} />
        {editing ? (
          allowHtml ? (
            <RichTextEditor
              value={String(value ?? '')}
              onChange={(html) => onChange(path, fieldKey, html)}
              disabled={!editing}
              minHeight={80}
            />
          ) : (
            // Texto plano: la plantilla PET pinta este campo sin interpretar HTML.
            <textarea
              value={String(value ?? '')}
              onChange={(e) => onChange(path, fieldKey, e.target.value)}
              className="input text-sm"
              rows={4}
              disabled={!editing}
            />
          )
        ) : allowHtml ? (
          <div
            className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700"
            dangerouslySetInnerHTML={{ __html: String(value ?? '') }}
          />
        ) : (
          <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700 whitespace-pre-wrap">
            {String(value ?? '') || <span className="text-gray-400 italic">Sin contenido</span>}
          </div>
        )}
      </div>
    );
  }

  // simple text
  return (
    <div className="mb-3">
      <FieldLabel fieldKey={fieldKey} />
      <input
        type="text"
        value={String(value ?? '')}
        onChange={(e) => onChange(path, fieldKey, e.target.value)}
        className="input"
        disabled={!editing}
      />
    </div>
  );
}

// ─── Component Palette Modal ──────────────────────────────────────────────────

interface PaletteModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (type: ComponentType) => void;
}

function PaletteModal({ open, onClose, onSelect }: PaletteModalProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<ComponentCategory | 'all'>('all');

  const filtered = COMPONENT_TYPES.filter((t) => {
    const matchesSearch =
      search === '' ||
      t.label.toLowerCase().includes(search.toLowerCase()) ||
      t.id.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || t.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Modal open={open} onClose={onClose} title="Añadir bloque de contenido" size="xl">
      <div className="flex flex-col gap-4">
        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar bloque..."
          className="input"
          autoFocus
        />

        {/* Category filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeCategory === 'all'
                ? 'bg-vida-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos ({COMPONENT_TYPES.length})
          </button>
          {(Object.keys(COMPONENT_CATEGORIES) as ComponentCategory[]).map((cat) => {
            const count = COMPONENT_TYPES.filter((t) => t.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-vida-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className="grayscale opacity-70">{COMPONENT_CATEGORIES[cat].icon}</span> {COMPONENT_CATEGORIES[cat].label} ({count})
              </button>
            );
          })}
        </div>

        {/* Grid de bloques como TARJETAS: cada una con una «miniatura» (icono
            grande sobre una banda que se ilumina al pasar el ratón) + nombre y
            descripción. Más visual y fácil de reconocer que una lista. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[460px] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <p className="col-span-full text-center text-gray-400 py-8">Sin resultados</p>
          ) : (
            filtered.map((type) => (
              <button
                key={type.id}
                onClick={() => { onSelect(type); onClose(); }}
                className="group flex flex-col text-left rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-vida-400 hover:shadow-md transition-all"
              >
                {/* Miniatura: icono grande sobre banda que colorea en hover */}
                <div className="h-20 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-vida-50 group-hover:to-vida-100 transition-colors">
                  <span className="text-4xl grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition">
                    {type.icon}
                  </span>
                </div>
                <div className="p-3 flex-1">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-vida-700 truncate">{type.label}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 leading-snug">{type.description}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PetPage() {
  const { hasPermission } = useAuthStore();
  const { organizations, selectedOrg, isLoading: orgLoading } = useOrganizationStore();

  const contentOrgs = organizations.filter(
    (org) =>
      org.permissions?.includes(PERMISSIONS.CONTENT_READ) ||
      org.permissions?.includes('*')
  );

  const canCreate = selectedOrg?.permissions?.includes(PERMISSIONS.CONTENT_CREATE) || selectedOrg?.permissions?.includes('*') || false;
  const canUpdate = selectedOrg?.permissions?.includes(PERMISSIONS.CONTENT_UPDATE) || selectedOrg?.permissions?.includes('*') || false;
  const canDelete = selectedOrg?.permissions?.includes(PERMISSIONS.CONTENT_DELETE) || selectedOrg?.permissions?.includes('*') || false;

  // ── View mode ─────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<'content' | 'nav' | 'footer' | 'site'>('content');

  // ── State ──────────────────────────────────────────────────────────────────

  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<CMSSection[]>([]);
  // Menú de navegación (para mostrar en las pestañas los textos REALES del menú/
  // submenú de la web, no solo los nombres internos de sección).
  const [navItems, setNavItems] = useState<Array<{ id: string; title?: string; submenu?: Array<{ path?: string; title?: string }> }>>([]);
  const [selectedMainSection, setSelectedMainSection] = useState<string>('');
  const [selectedSubSection, setSelectedSubSection] = useState<string>('');
  const [blocks, setBlocks] = useState<ContentDocument[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<ContentDocument | null>(null);
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  // Editing
  const [editingBlock, setEditingBlock] = useState<ContentDocument | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modals
  const [showPalette, setShowPalette] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<ContentDocument | null>(null);

  // Rename inline (blocks)
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameRef = useRef<HTMLInputElement>(null);

  // Section label editing
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionLabelValue, setSectionLabelValue] = useState('');
  const sectionLabelRef = useRef<HTMLInputElement>(null);

  // ── Preview en vivo ──────────────────────────────────────────────────────────
  // La URL pública de la web PET no vive en el modelo de la org (public_url es el
  // del storage), así que la fija el usuario una vez y se guarda en localStorage
  // por organización. `previewKey` fuerza recargar el iframe (al guardar / al pulsar).
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!selectedOrg?._id) return;
    // Prioridad: override manual (localStorage) > URL autodetectada por el backend
    // (config.public_site_url) > dominio configurado. Así normalmente no hay que
    // pegar nada: la web PET se autodetecta sola en cuanto carga contenido.
    const saved = localStorage.getItem(`pet_preview_url_${selectedOrg._id}`);
    const auto = selectedOrg.config?.public_site_url;
    const domain = selectedOrg.config?.domain;
    setPreviewUrl(
      saved ||
        auto ||
        (domain ? `https://${domain.replace(/^https?:\/\//, '')}` : '')
    );
  }, [selectedOrg?._id, selectedOrg?.config?.public_site_url, selectedOrg?.config?.domain]);

  const savePreviewUrl = (url: string) => {
    setPreviewUrl(url);
    if (selectedOrg?._id) localStorage.setItem(`pet_preview_url_${selectedOrg._id}`, url);
  };

  // Normaliza a URL absoluta (añade https:// si falta) para el iframe.
  const previewSrc = previewUrl
    ? (/^https?:\/\//i.test(previewUrl) ? previewUrl : `https://${previewUrl}`)
    : '';

  // ── Load sections ──────────────────────────────────────────────────────────

  const loadSections = useCallback(() => {
    contentApi.getSections(selectedOrg?._id)
      .then((res) => setSections(res.data.sections || []))
      .catch(() => toast.error('Error al cargar secciones'))
      .finally(() => setLoading(false));
  }, [selectedOrg?._id]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  // Reset on org change
  useEffect(() => {
    setSelectedMainSection('');
    setSelectedSubSection('');
    setBlocks([]);
    setSelectedBlock(null);
    setEditingBlock(null);
    setIsEditing(false);
  }, [selectedOrg?._id]);

  // Auto-select first section once sections load (or after org change reset)
  useEffect(() => {
    if (sections.length > 0 && !selectedMainSection) {
      setSelectedMainSection(sections[0].id);
    }
  }, [sections, selectedMainSection]);

  // ── Active section id ──────────────────────────────────────────────────────

  const activeSectionId = (() => {
    const main = sections.find((s) => s.id === selectedMainSection);
    if (main?.subsections) return selectedSubSection || null;
    return selectedMainSection || null;
  })();

  // ── Click-to-edit: la vista previa muestra la sección actual en modo edición,
  // y clicar un bloque en la web lo selecciona aquí (y viceversa lo resalta). ──
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const blocksRef = useRef<ContentDocument[]>([]);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);

  // URL del iframe: misma página que la sección editada + ?vidaEdit=1.
  const previewIframeSrc = (() => {
    if (!previewSrc) return '';
    const base = previewSrc.replace(/\/+$/, '');
    const path = SECTION_TO_PATH[activeSectionId ?? ''] ?? '';
    const url = path ? `${base}/es/${path}` : `${base}/es`;
    return `${url}?vidaEdit=1`;
  })();

  // Clic en un bloque de la web → seleccionarlo en el panel.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; blockId?: string } | null;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'vida:selectBlock' && d.blockId) {
        const b = blocksRef.current.find((x) => x._id === d.blockId);
        if (b) {
          setSelectedBlock(b);
          setIsEditing(false);
          setEditingBlock(null);
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Resalta (y hace scroll) un bloque en la web desde el panel.
  const highlightInPreview = (blockId: string | null) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'vida:highlightBlock', blockId }, '*');
  };

  // Bloque seleccionado en el panel → resaltarlo en la web.
  useEffect(() => {
    highlightInPreview(selectedBlock?._id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock]);

  // Carga el menú de navegación (para etiquetar las secciones con su texto real).
  useEffect(() => {
    if (!selectedOrg?._id) return;
    contentApi.getNavConfig()
      .then((res) => setNavItems(res.data?.data?.items || []))
      .catch(() => setNavItems([]));
  }, [selectedOrg?._id]);

  // Normaliza una ruta (quita host, barra inicial, locale y barra final).
  const normPath = (p?: string) =>
    (p || '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '').replace(/^(es|en)\//i, '').replace(/\/+$/, '').toLowerCase();

  // Ruta → texto real del submenú.
  const pathToNavTitle: Record<string, string> = {};
  for (const it of navItems) for (const s of it.submenu || []) if (s?.path && s?.title) pathToNavTitle[normPath(s.path)] = s.title;

  // Devuelve el texto REAL del menú para una sección (o null si no hay match).
  const navTitleFor = (id: string): string | null => {
    const path = SECTION_TO_PATH[id];
    if (path) {
      const t = pathToNavTitle[normPath(path)];
      if (t) return t;
    }
    // Sección principal (desplegable): casa por solape de sus subsecciones.
    const mainSec = sections.find((s) => s.id === id && s.subsections?.length);
    if (mainSec?.subsections) {
      const subPaths = new Set(
        mainSec.subsections.map((ss) => normPath(SECTION_TO_PATH[ss.id] || '')).filter(Boolean)
      );
      for (const it of navItems) {
        if ((it.submenu || []).some((s) => subPaths.has(normPath(s?.path)))) return it.title || null;
      }
    }
    return null;
  };

  // ── Load blocks ────────────────────────────────────────────────────────────

  const loadBlocks = useCallback(async () => {
    if (!selectedOrg || !activeSectionId) {
      setBlocks([]);
      return;
    }
    setLoadingBlocks(true);
    try {
      const res = await contentApi.getContents(selectedOrg._id, activeSectionId);
      const sorted = [...(res.data.data || [])].sort(
        (a: ContentDocument, b: ContentDocument) => (a.order ?? 0) - (b.order ?? 0)
      );
      setBlocks(sorted);
    } catch {
      toast.error('Error al cargar bloques');
    } finally {
      setLoadingBlocks(false);
    }
  }, [selectedOrg, activeSectionId]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const updateNestedValue = (
    obj: Record<string, unknown>,
    path: (string | number)[],
    key: string,
    value: unknown
  ): Record<string, unknown> => {
    if (path.length === 0) return { ...obj, [key]: value };
    const [first, ...rest] = path;
    const current = obj[first as string];
    if (Array.isArray(current)) {
      const arr = [...current];
      const idx = rest[0] as number;
      const restPath = rest.slice(1);
      arr[idx] =
        restPath.length === 0
          ? { ...(arr[idx] as Record<string, unknown>), [key]: value }
          : updateNestedValue(arr[idx] as Record<string, unknown>, restPath, key, value);
      return { ...obj, [first]: arr };
    }
    if (typeof current === 'object' && current !== null) {
      return { ...obj, [first]: updateNestedValue(current as Record<string, unknown>, rest, key, value) };
    }
    return obj;
  };

  const handleFieldChange = (path: (string | number)[], key: string, value: unknown) => {
    setEditingBlock((prev) => {
      if (!prev) return prev;
      return { ...prev, content: updateNestedValue(prev.content, path, key, value) };
    });
  };

  // ── Block actions ──────────────────────────────────────────────────────────

  const handleSelectBlock = (block: ContentDocument) => {
    if (isEditing) return; // prevent deselect while editing
    setSelectedBlock(block);
    setEditingBlock(null);
    setIsEditing(false);
  };

  const handleEdit = () => {
    if (!selectedBlock) return;
    setEditingBlock(JSON.parse(JSON.stringify(selectedBlock)));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditingBlock(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!editingBlock || !selectedOrg) return;
    setIsSaving(true);
    try {
      await contentApi.updateContent(selectedOrg._id, editingBlock._id, {
        content: editingBlock.content,
      });
      toast.success('Cambios guardados');
      setIsEditing(false);
      setEditingBlock(null);
      await loadBlocks();
      // re-select with updated data
      setSelectedBlock((prev) =>
        prev ? { ...prev, content: editingBlock.content } : prev
      );
      // Refrescar el preview en vivo con los cambios recién guardados.
      setPreviewKey((k) => k + 1);
    } catch {
      toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // Move block up/down
  const handleMove = async (blockId: string, direction: 'up' | 'down') => {
    if (!selectedOrg) return;
    const idx = blocks.findIndex((b) => b._id === blockId);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === blocks.length - 1) return;

    const newBlocks = [...blocks];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newBlocks[idx], newBlocks[swapIdx]] = [newBlocks[swapIdx], newBlocks[idx]];

    // Optimistic update
    setBlocks(newBlocks);

    const items = newBlocks.map((b, i) => ({ id: b._id, order: i }));
    try {
      await contentApi.reorderContents(selectedOrg._id, items);
    } catch {
      toast.error('Error al reordenar');
      loadBlocks();
    }
  };

  // Duplicate
  const handleDuplicate = async (blockId: string) => {
    if (!selectedOrg || !canCreate) return;
    try {
      await contentApi.duplicateContent(selectedOrg._id, blockId);
      toast.success('Bloque duplicado');
      await loadBlocks();
    } catch {
      toast.error('Error al duplicar');
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!blockToDelete || !selectedOrg) return;
    try {
      await contentApi.deleteContent(selectedOrg._id, blockToDelete._id);
      toast.success('Bloque eliminado');
      if (selectedBlock?._id === blockToDelete._id) {
        setSelectedBlock(null);
        setEditingBlock(null);
        setIsEditing(false);
      }
      setShowDeleteDialog(false);
      setBlockToDelete(null);
      await loadBlocks();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  // Create from palette
  const handleAddBlock = async (type: ComponentType) => {
    if (!selectedOrg || !activeSectionId) return;
    try {
      const res = await contentApi.createContent(selectedOrg._id, {
        section: activeSectionId,
        component: type.id,
        content: type.defaultContent,
      });
      toast.success(`Bloque "${type.label}" añadido`);
      await loadBlocks();
      // auto-select the new block
      setSelectedBlock(res.data.data);
    } catch {
      toast.error('Error al crear bloque');
    }
  };

  // Rename (component label)
  const startRename = (block: ContentDocument) => {
    setRenamingId(block._id);
    setRenameValue(block.component);
    setTimeout(() => renameRef.current?.focus(), 50);
  };

  const commitRename = async (blockId: string) => {
    if (!selectedOrg || !renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await contentApi.updateContent(selectedOrg._id, blockId, { component: renameValue.trim() });
      setRenamingId(null);
      await loadBlocks();
    } catch {
      toast.error('Error al renombrar');
    }
  };

  // Section/subsection label editing.
  // Para las subsecciones con entrada en el menú, renombrar edita el TEXTO DEL
  // MENÚ que ve el visitante (no el nombre interno). Se pre-rellena con ese texto.
  const startEditSectionLabel = (sectionId: string, currentLabel: string) => {
    setEditingSectionId(sectionId);
    const path = SECTION_TO_PATH[sectionId];
    const menuText = path ? pathToNavTitle[normPath(path)] : undefined;
    setSectionLabelValue(menuText || currentLabel);
    setTimeout(() => sectionLabelRef.current?.focus(), 50);
  };

  const commitSectionLabel = async () => {
    const id = editingSectionId;
    const value = sectionLabelValue.trim();
    if (!selectedOrg || !id || !value) {
      setEditingSectionId(null);
      return;
    }

    // ¿Es una subsección con entrada en el menú? → editar el TEXTO DEL MENÚ.
    const path = SECTION_TO_PATH[id];
    if (path) {
      const np = normPath(path);
      let matched = false;
      const updatedNav = navItems.map((it) => ({
        ...it,
        submenu: (it.submenu || []).map((s) => {
          if (normPath(s?.path) === np) { matched = true; return { ...s, title: value }; }
          return s;
        }),
      }));
      if (matched) {
        setNavItems(updatedNav);
        setEditingSectionId(null);
        try {
          await contentApi.saveNavConfig({ items: updatedNav });
          toast.success('Texto del menú actualizado');
        } catch {
          toast.error('Error al guardar el menú');
          contentApi.getNavConfig().then((res) => setNavItems(res.data?.data?.items || [])).catch(() => {});
        }
        return;
      }
    }

    // Fallback (sin entrada de menú): renombra la etiqueta interna, como antes.
    const updated = sections.map(s => {
      if (s.id === id) return { ...s, label: value };
      if (s.subsections) {
        return {
          ...s,
          subsections: s.subsections.map(sub =>
            sub.id === id ? { ...sub, label: value } : sub
          ),
        };
      }
      return s;
    });
    setSections(updated);
    setEditingSectionId(null);
    try {
      await contentApi.saveSections(selectedOrg._id, updated);
      toast.success('Nombre de sección actualizado');
    } catch {
      toast.error('Error al guardar secciones');
      loadSections();
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (!hasPermission(PERMISSIONS.CONTENT_READ) && !hasPermission('*')) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900">Acceso denegado</h2>
          <p className="mt-2 text-gray-600">No tienes permisos para gestionar contenidos.</p>
        </div>
      </div>
    );
  }

  if (loading || orgLoading) return <PageLoader />;

  if (contentOrgs.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={DocumentTextIcon}
          title="Sin acceso a contenidos"
          description="No tienes permisos de contenido en ninguna organización."
        />
      </div>
    );
  }

  const hasContentAccess =
    selectedOrg?.permissions?.includes(PERMISSIONS.CONTENT_READ) ||
    selectedOrg?.permissions?.includes('*');

  if (!hasContentAccess) {
    return (
      <div className="p-6">
        <EmptyState
          icon={DocumentTextIcon}
          title="Sin acceso a contenidos"
          description="La organización seleccionada no tiene permisos de contenido."
        />
      </div>
    );
  }

  const currentMain = sections.find((s) => s.id === selectedMainSection);
  const hasSubsections = !!(currentMain?.subsections?.length);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Page Builder · PET-VIDA</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="font-medium text-vida-600">{selectedOrg?.name}</span>
            {activeSectionId && (
              <> &rsaquo; <span className="text-gray-700">{activeSectionId}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['content', 'nav', 'footer', 'site'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === mode
                  ? 'bg-vida-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {mode === 'content' ? 'Contenido' : mode === 'nav' ? 'Menu' : mode === 'footer' ? 'Footer' : 'Páginas'}
            </button>
          ))}
          {viewMode === 'content' && (
            <button
              onClick={() => setShowPreview((v) => !v)}
              className={`ml-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                showPreview
                  ? 'bg-vida-600 text-white border-vida-600'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border-gray-200'
              }`}
              title="Mostrar/ocultar la vista previa de la web"
            >
              <EyeIcon className="w-4 h-4 inline -mt-0.5 mr-1" />
              Vista previa
            </button>
          )}
          <button
            onClick={() => { loadSections(); loadBlocks(); }}
            className="btn-secondary ml-2"
            title="Recargar secciones y bloques"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'nav' && <NavConfigEditor />}
      {viewMode === 'footer' && <FooterConfigEditor />}
      {viewMode === 'site' && <PetSiteEditor />}

      {viewMode === 'content' && <>
      {/* ── Section tabs ── */}
      <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2">
        {sections.map((section) => (
          <div key={section.id} className="flex items-center">
            {editingSectionId === section.id ? (
              <input
                ref={sectionLabelRef}
                value={sectionLabelValue}
                onChange={(e) => setSectionLabelValue(e.target.value)}
                onBlur={commitSectionLabel}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitSectionLabel();
                  if (e.key === 'Escape') setEditingSectionId(null);
                }}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border-2 border-vida-400 bg-white outline-none w-32"
              />
            ) : (
              <button
                onClick={() => {
                  setSelectedMainSection(section.id);
                  setSelectedSubSection('');
                  setSelectedBlock(null);
                  setEditingBlock(null);
                  setIsEditing(false);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  // Las categorías con subsecciones (Qué ver/Qué hacer…) son fijas.
                  if (canUpdate && !section.subsections?.length) startEditSectionLabel(section.id, section.label);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors group/tab ${
                  selectedMainSection === section.id
                    ? 'bg-vida-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                title={section.subsections?.length ? 'Categoría del menú (fija)' : 'Doble clic para editar el texto del menú'}
              >
                <FolderIcon className="w-3.5 h-3.5" />
                {(() => {
                  const real = navTitleFor(section.id);
                  return real && real !== section.label
                    ? <span>{real}<span className="ml-1 opacity-50 font-normal">({section.label})</span></span>
                    : <span>{real || section.label}</span>;
                })()}
                {canUpdate && !section.subsections?.length && (
                  <PencilIcon
                    className={`w-3 h-3 opacity-0 group-hover/tab:opacity-60 transition-opacity ${
                      selectedMainSection === section.id ? 'text-white' : 'text-gray-400'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditSectionLabel(section.id, section.label);
                    }}
                  />
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* ── Subsection tabs ── */}
      {hasSubsections && (
        <div className="px-6 py-2 border-b border-gray-100 bg-white flex flex-wrap gap-2">
          {currentMain?.subsections?.map((sub) => (
            <div key={sub.id} className="flex items-center">
              {editingSectionId === sub.id ? (
                <input
                  ref={sectionLabelRef}
                  value={sectionLabelValue}
                  onChange={(e) => setSectionLabelValue(e.target.value)}
                  onBlur={commitSectionLabel}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitSectionLabel();
                    if (e.key === 'Escape') setEditingSectionId(null);
                  }}
                  className="px-3 py-1 rounded text-sm border-2 border-vida-400 bg-white outline-none w-32"
                />
              ) : (
                <button
                  onClick={() => {
                    setSelectedSubSection(sub.id);
                    setSelectedBlock(null);
                    setEditingBlock(null);
                    setIsEditing(false);
                  }}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    if (canUpdate) startEditSectionLabel(sub.id, sub.label);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-colors group/sub ${
                    selectedSubSection === sub.id
                      ? 'bg-vida-500 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                  title="Doble clic para editar el texto del menú"
                >
                  {(() => {
                    const real = navTitleFor(sub.id);
                    return real && real !== sub.label
                      ? <span>{real}<span className="ml-1 opacity-50 font-normal">({sub.label})</span></span>
                      : <span>{real || sub.label}</span>;
                  })()}
                  {canUpdate && (
                    <PencilIcon
                      className={`w-3 h-3 opacity-0 group-hover/sub:opacity-60 transition-opacity ${
                        selectedSubSection === sub.id ? 'text-white' : 'text-gray-400'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditSectionLabel(sub.id, sub.label);
                      }}
                    />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Main 3-col layout ── */}
      <div className="flex flex-1 overflow-hidden min-h-0">

        {/* ── Col 1: Block list ── */}
        <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Squares2X2Icon className="w-4 h-4 text-gray-400" />
              Bloques
              {blocks.length > 0 && (
                <span className="ml-1 text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5">
                  {blocks.length}
                </span>
              )}
            </span>
            {canCreate && activeSectionId && (
              <button
                onClick={() => setShowPalette(true)}
                className="btn-primary text-xs py-1 px-2"
              >
                <PlusIcon className="w-3.5 h-3.5 mr-1" />
                Añadir
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {!activeSectionId ? (
              <div className="p-6 text-center text-sm text-gray-400">
                Selecciona una sección
              </div>
            ) : loadingBlocks ? (
              <div className="p-6 text-center text-sm text-gray-400">Cargando...</div>
            ) : blocks.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                Sin bloques.{canCreate && (
                  <button
                    onClick={() => setShowPalette(true)}
                    className="block mx-auto mt-2 text-vida-600 hover:underline"
                  >
                    + Añadir el primero
                  </button>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {blocks.map((block, idx) => {
                  const typeInfo = getComponentType(block.component);
                  const blockImg = getBlockImage(block.content);
                  const isSelected = selectedBlock?._id === block._id;
                  return (
                    <li
                      key={block._id}
                      onClick={() => handleSelectBlock(block)}
                      onMouseEnter={() => highlightInPreview(block._id)}
                      onMouseLeave={() => highlightInPreview(selectedBlock?._id ?? null)}
                      className={`group flex items-start gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-vida-50 border-l-4 border-vida-500'
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      {/* Miniatura: imagen real del bloque, o su icono si no tiene */}
                      <div className="w-12 h-10 rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                        {blockImg ? (
                          <img src={blockImg} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg grayscale opacity-70">{typeInfo?.icon ?? '📦'}</span>
                        )}
                      </div>

                      {/* name + type */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {renamingId === block._id ? (
                            <input
                              ref={renameRef}
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onBlur={() => commitRename(block._id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitRename(block._id);
                                if (e.key === 'Escape') setRenamingId(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs font-medium border-b border-vida-400 bg-transparent outline-none w-full"
                            />
                          ) : (
                            <span className="text-xs font-medium text-gray-800 truncate">
                              {getBlockPreview(block.content) || typeInfo?.label || block.component}
                            </span>
                          )}
                        </div>
                        {/* Debajo, el TIPO de bloque (pequeño): así se reconoce el
                            bloque por su contenido arriba y por su tipo abajo. */}
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                          {typeInfo?.label ?? block.component}
                        </p>
                      </div>

                      {/* actions (visible on hover or selected) */}
                      <div
                        className={`flex items-center gap-0.5 flex-shrink-0 ${
                          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        } transition-opacity`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleMove(block._id, 'up')}
                          disabled={idx === 0}
                          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                          title="Subir"
                        >
                          <ChevronUpIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMove(block._id, 'down')}
                          disabled={idx === blocks.length - 1}
                          className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                          title="Bajar"
                        >
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        </button>
                        {canUpdate && (
                          <button
                            onClick={() => startRename(block)}
                            className="p-0.5 text-gray-400 hover:text-vida-600"
                            title="Renombrar"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canCreate && (
                          <button
                            onClick={() => handleDuplicate(block._id)}
                            className="p-0.5 text-gray-400 hover:text-blue-600"
                            title="Duplicar"
                          >
                            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => {
                              setBlockToDelete(block);
                              setShowDeleteDialog(true);
                            }}
                            className="p-0.5 text-gray-400 hover:text-red-600"
                            title="Eliminar"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ── Col 2+3: Editor panel ── */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {!selectedBlock ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <DocumentTextIcon className="w-16 h-16 mb-4 text-gray-200" />
              <p className="text-sm">Selecciona un bloque para editar su contenido</p>
              {canCreate && activeSectionId && (
                <button
                  onClick={() => setShowPalette(true)}
                  className="mt-4 btn-primary text-sm"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Añadir bloque
                </button>
              )}
            </div>
          ) : (
            <div className="p-6 max-w-3xl mx-auto">
              {/* Block header */}
              <div className="flex items-start justify-between mb-6 gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl grayscale opacity-70">
                    {getComponentType(selectedBlock.component)?.icon ?? '📦'}
                  </span>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {getComponentType(selectedBlock.component)?.label ?? selectedBlock.component}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {getComponentType(selectedBlock.component)?.description ?? 'Componente personalizado'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isEditing && canUpdate && (
                    <button onClick={handleEdit} className="btn-primary text-sm">
                      <PencilIcon className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                  )}
                  {isEditing && (
                    <>
                      <button onClick={handleCancelEdit} className="btn-secondary text-sm">
                        <XMarkIcon className="w-4 h-4 mr-1" />
                        Cancelar
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="btn-primary text-sm"
                      >
                        <CheckIcon className="w-4 h-4 mr-1" />
                        {isSaving ? 'Guardando...' : 'Guardar'}
                      </button>
                    </>
                  )}
                  {!isEditing && canDelete && (
                    <button
                      onClick={() => {
                        setBlockToDelete(selectedBlock);
                        setShowDeleteDialog(true);
                      }}
                      className="btn-danger text-sm"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Fields */}
              <div className="card p-5">
                {isEditing && editingBlock
                  ? Object.entries(editingBlock.content).map(([key, value]) => (
                      <FieldRenderer
                        key={key}
                        fieldKey={key}
                        value={value}
                        path={[]}
                        editing={true}
                        canUpdate={canUpdate}
                        orgId={selectedOrg!._id}
                        sectionId={activeSectionId ?? ''}
                        allowHtml={ALLOW_HTML_COMPONENTS.has(selectedBlock.component)}
                        onChange={handleFieldChange}
                      />
                    ))
                  : Object.entries(selectedBlock.content).map(([key, value]) => (
                      <FieldRenderer
                        key={key}
                        fieldKey={key}
                        value={value}
                        path={[]}
                        editing={false}
                        canUpdate={false}
                        orgId={selectedOrg!._id}
                        sectionId={activeSectionId ?? ''}
                        allowHtml={ALLOW_HTML_COMPONENTS.has(selectedBlock.component)}
                        onChange={() => {}}
                      />
                    ))}

                {Object.keys(selectedBlock.content).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">Sin campos de contenido</p>
                )}
              </div>

              {/* Meta info — solo lo útil para un usuario normal (sin ID ni orden interno). */}
              <div className="mt-4 text-xs text-gray-400">
                <span>Actualizado el {new Date(selectedBlock.updatedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Panel de vista previa en vivo ── */}
        {showPreview && (
          <div className="w-[440px] flex-shrink-0 border-l border-gray-200 bg-white flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 bg-gray-50">
              <input
                type="text"
                value={previewUrl}
                onChange={(e) => savePreviewUrl(e.target.value)}
                placeholder="https://tu-web-pet.vercel.app"
                className="input text-xs flex-1 py-1"
                title="URL pública de la web PET-VIDA de esta organización"
              />
              <button
                onClick={() => setPreviewKey((k) => k + 1)}
                className="btn-secondary p-1.5"
                title="Recargar vista previa"
                disabled={!previewSrc}
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
              {previewSrc && (
                <a
                  href={previewSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary p-1.5"
                  title="Abrir en pestaña nueva"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              )}
            </div>
            {previewSrc ? (
              <iframe
                ref={iframeRef}
                key={`${activeSectionId ?? 'home'}-${previewKey}`}
                src={previewIframeSrc}
                className="flex-1 w-full border-0"
                title="Vista previa PET-VIDA"
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm p-6 text-center">
                <EyeIcon className="w-10 h-10 mb-3 text-gray-200" />
                Pega arriba la URL pública de la web PET-VIDA de esta organización
                para ver los cambios en vivo. Se recordará para la próxima vez.
              </div>
            )}
            <p className="px-3 py-1.5 text-[11px] text-gray-400 border-t border-gray-100">
              <span className="font-medium text-vida-600">Haz clic en un bloque de la web</span> para
              editarlo aquí al lado. Los cambios aparecen tras <span className="font-medium">Guardar</span>.
            </p>
          </div>
        )}
      </div>

      {/* ── Palette modal ── */}
      <PaletteModal
        open={showPalette}
        onClose={() => setShowPalette(false)}
        onSelect={handleAddBlock}
      />

      {/* ── Delete confirm ── */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setBlockToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar bloque"
        message={`¿Seguro que deseas eliminar el bloque "${blockToDelete?.component}"? Esta acción no se puede deshacer.`}
        variant="danger"
        confirmText="Eliminar"
      />
      </>}
    </div>
  );
}
